/**
 * review-toggle.js
 */

import React from 'react';
import { useMediaQuery, Tooltip, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ToggleButtonGroup, ToggleButton as MuiToggleButton } from '@mui/material';
import { Types } from 'utils/constants';
import useRecordStore from 'contexts/record-store';
import Icon from 'components/icon';
import Dialog from 'components/dialog';


const useStyles = makeStyles(theme => ({
  toggleGroup: {
    [theme.breakpoints.down('sm')]: {
      flex: 1,
    },
    '& .MuiToggleButton-root': {
      flex: 1,
      padding: `7px 12px`,
      textTransform: 'none',
      '&.Mui-selected': {
        backgroundColor: theme.palette.sys.primaryContainer
      }
    },
    '& .button-label': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      '& .text-label': {
        marginTop: 4,
        lineHeight: '1.2',
        whiteSpace: 'nowrap',
        [theme.breakpoints.up('sm')]: {
          display: 'none'
        }
      }
    },
  },
  textField: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
    }
  }
}));


const ToggleButton = React.forwardRef((props, ref) => {
  const {
    label,
    onClick,
    iconType,
    iconClass,
    value,
    ...rest
  } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });

  return (
    <Tooltip
      arrow
      placement='top'
      title={isMobile ? '' : label || ''}
      style={{ display: 'flex' }}
    >
      <MuiToggleButton 
        {...rest}
        ref={ref}
        size='small' 
        value={value}
        onClick={onClick}
        data-testid={`review-toggle-${value}`}
      >
        <span className='button-label'>
          <Icon type={iconType} className={iconClass} />
          <span className='text-label'>{label}</span>
        </span>
      </MuiToggleButton>
    </Tooltip>
  );
});


export default function ReviewToggle(props) {
  const { 
    type,
    id, 
    drawerState,
  } = props;
  const classes = useStyles();
  const recordStore = useRecordStore();
  const record = recordStore.useRecord(type, id);
  const [isRejectedOpen, setIsRejectedOpen] = React.useState(false);
  const [rejectedReason, setRejectedReason] = React.useState('');
  const [isReasonValid, setIsReasonValid] = React.useState(true);
  const recordRejectedReason = record.review.ornull ? record.review?.rejectedReason?.get() : null;


  React.useEffect(() => {
    setRejectedReason(recordRejectedReason);
  }, [recordRejectedReason]);


  const saveReview = async (status, reason) => {
    drawerState.loading.content.set(true);
    await recordStore.setReviewStatus(type, id, status, reason);
    drawerState.loading.content.set(false);
  }


  const onChangeReviewStatus = async (status) => {
    if (status === Types.ReviewStatus.REJECTED) {
      setIsRejectedOpen(true);
    } else {
      saveReview(status);
    }
  }


  if (!record || !record.review.get()) return null;
  
  return (
    <>
      <ToggleButtonGroup
        exclusive
        value={record.review.status.get()}
        className={classes.toggleGroup}
      >
        {/* NOT REVIEWED */}
        <ToggleButton
          label='Not Reviewed'
          iconType='not-reviewed'
          iconClass='icon-incomplete'
          value={Types.ReviewStatus.NOT_REVIEWED}
          onClick={() => onChangeReviewStatus(Types.ReviewStatus.NOT_REVIEWED)}
        />

        {/* REVIEWED */}
        <ToggleButton
          label='Accepted'
          iconType='accepted'
          iconClass='icon-complete'
          value={Types.ReviewStatus.ACCEPTED}
          onClick={() => onChangeReviewStatus(Types.ReviewStatus.ACCEPTED)}
        />

        {/* REJECTED */}
        <ToggleButton
          label='Rejected'
          iconType='rejected'
          iconClass='icon-error'
          value={Types.ReviewStatus.REJECTED}
          onClick={() => onChangeReviewStatus(Types.ReviewStatus.REJECTED)}
        />
      </ToggleButtonGroup>

      {/* rejected input dialog */}
      <Dialog
        maxWidth='sm'
        fullWidth
        title='Rejected Reason'
        titleIcon='rejected'
        open={isRejectedOpen}
        onClose={() => {
          setIsRejectedOpen(false);
          setRejectedReason(recordRejectedReason || '')
        }}
        onOk={() => {
          if (rejectedReason === '') {
            setIsReasonValid(false);
          } else {
            setIsReasonValid(true);
            setIsRejectedOpen(false);
            saveReview(Types.ReviewStatus.REJECTED, rejectedReason);
          }
        }}
      >
        <TextField 
          variant='outlined'
          size='small'
          fullWidth
          multiline
          rows={10}
          placeholder='Reason for rejecting this record...'
          value={rejectedReason}
          onChange={e => setRejectedReason(e.target.value)}
          className={classes.textField}
          error={!isReasonValid}
          inputProps={{ 'data-testid': 'rejection-reason-input' }}
        />
        {!isReasonValid && 
        <Typography color='error' style={{ marginTop: 16 }}>Please enter a rejection reason.</Typography>}
      </Dialog>
    </>
  )
}